const SYENITE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/23c5664e-366b-4aed-89ee-66b59e602110/images/1b03dbde7f0aa97329e4c17fc2953ffd5e2c27acc62dbecbe147579281ff72bd.png";

import { Reveal, StaggerGroup, StaggerItem } from "@/components/site/Reveal";

const specs = [
  { k: "Densità", v: "2,73 g/cm³", note: "Roccia intrusiva felsica" },
  { k: "Resistenza compressiva", v: "≈ 220 MPa", note: "Eccellente per uso strutturale" },
  { k: "Assorbimento d'acqua", v: "0,15 %", note: "Bassissima porosità" },
  { k: "Resistenza al gelo", v: "≥ 100 cicli", note: "Adatta ad esterni alpini" },
  { k: "Composizione", v: "Plagioclasio · Ortoclasio · Anfiboli", note: "Senza quarzo libero" },
  { k: "Finiture", v: "Lucida · Fiammata · Bocciardata · Spazzolata", note: "Lavorazione tradizionale" },
];

const uses = [
  "Pavimentazioni urbane",
  "Rivestimenti monumentali",
  "Cordoli e basamenti",
  "Lapidi e architettura funeraria",
  "Elementi architettonici a vista",
];

export default function Sienite() {
  return (
    <section id="sienite" data-testid="sienite-section" className="relative bg-[var(--surface-1)] section-pad section-gap border-y border-[var(--border-subtle)]">
      <div className="grid md:grid-cols-12 gap-10 mb-16">
        <Reveal className="md:col-span-4">
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-neutral-500 mb-3">
            02 / Materiale
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-neutral-50 leading-[0.95]">
            La sienite,
            <br />
            <span className="text-[var(--accent)] italic font-editorial font-normal">
              così come la cava.
            </span>
          </h2>
        </Reveal>
        <Reveal className="md:col-span-7 md:col-start-6 self-end" delay={0.15}>
          <p className="text-neutral-400 text-base md:text-lg leading-relaxed max-w-2xl">
            Pietra magmatica intrusiva di colore grigio-verdognolo con riflessi azzurro-acciaio,
            la <strong className="text-neutral-200 font-normal">sienite della Balma</strong>{" "}
            — il granito biellese di pregio — deve la sua fama alla tessitura cristallina
            compatta e alla durabilità. Estratta storicamente in blocchi grezzi e oggi prodotta
            in serie limitate da GMB Graniti s.r.l. nell'ultima cava attiva della Valle Cervo,
            si lavora in lastre, pavimentazioni, rivestimenti, cordoli, lapidi e monumenti.
          </p>
        </Reveal>
      </div>

      {/* Bento layout */}
      <StaggerGroup className="grid md:grid-cols-12 gap-px bg-[var(--border-subtle)]" stagger={0.07}>
        {/* Image block (left, full height) */}
        <StaggerItem className="md:col-span-7 md:row-span-3 relative aspect-[4/5] md:aspect-auto bg-[var(--surface-2)] overflow-hidden min-h-[420px]">
          <img
            src={SYENITE_IMG}
            alt="Macro sienite"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <p className="text-[0.65rem] tracking-[0.3em] uppercase text-neutral-300 mb-2">
              Tessitura · Macrofotografia
            </p>
            <p className="font-display text-2xl md:text-3xl text-white">
              Sienite Verde Balma
            </p>
          </div>
        </StaggerItem>

        {/* Specs grid right of image (1x3 stacked) */}
        {specs.slice(0, 3).map((s, i) => (
          <StaggerItem
            key={i}
            data-testid={`sienite-spec-${i}`}
            className="md:col-span-5 bg-[var(--surface-1)] p-6 md:p-8 hover-lift border border-transparent hover:border-[var(--accent)] flex flex-col justify-center"
          >
            <p className="text-[0.65rem] tracking-[0.3em] uppercase text-neutral-500">
              {s.k}
            </p>
            <p className="font-display text-2xl md:text-3xl text-neutral-100 mt-3 leading-tight">
              {s.v}
            </p>
            <p className="text-xs text-neutral-500 mt-3 leading-relaxed">{s.note}</p>
          </StaggerItem>
        ))}

        {/* Specs row below (3 columns full-width) */}
        {specs.slice(3).map((s, i) => (
          <StaggerItem
            key={i + 3}
            data-testid={`sienite-spec-${i + 3}`}
            className="md:col-span-4 bg-[var(--surface-1)] p-6 md:p-8 hover-lift border border-transparent hover:border-[var(--accent)]"
          >
            <p className="text-[0.65rem] tracking-[0.3em] uppercase text-neutral-500">
              {s.k}
            </p>
            <p className="font-display text-2xl md:text-3xl text-neutral-100 mt-3 leading-tight">
              {s.v}
            </p>
            <p className="text-xs text-neutral-500 mt-3 leading-relaxed">{s.note}</p>
          </StaggerItem>
        ))}

        {/* Usage full-width */}
        <StaggerItem className="md:col-span-12 bg-[var(--surface-1)] p-6 md:p-10">
          <p className="text-[0.65rem] tracking-[0.3em] uppercase text-neutral-500 mb-6">
            Applicazioni
          </p>
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            {uses.map((u, i) => (
              <span
                key={i}
                className="font-display text-xl md:text-2xl text-neutral-200 flex items-center gap-3"
              >
                <span className="block w-1.5 h-1.5 rotate-45 bg-[var(--accent)]" />
                {u}
              </span>
            ))}
          </div>
        </StaggerItem>
      </StaggerGroup>
    </section>
  );
}
